import { useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Check, Plus, X } from 'lucide-react';
import { AcaoPrimaria } from '../../componentes/acao-primaria';
import { Botao, Selo } from '../../componentes/base';
import { Vazio } from '../../componentes/estados';
import { AreaSensivel, Formulario, Rotulo, Selecao, type DefCampo } from '../../componentes/formulario';
import { Confirmar, Drawer, Modal } from '../../componentes/sobreposicao';
import { Tabela, type Coluna } from '../../componentes/tabela';
import { useToast } from '../../componentes/toast';
import { mensagemDeErro, post } from '../../nucleo/api';
import { data, dataHora, nomePessoa, textoLivre } from '../../nucleo/formato';
import { useSessao } from '../../nucleo/sessao';
import { COR_AJUSTE, ESTADOS_AJUSTE, invalidarPonto, type Ajuste, type EstadoAjuste } from './tipos';

/** Ajustes de ponto: incluir a marcação esquecida ou desconsiderar uma, com justificativa e a decisão de quem aprova. */
export function Ajustes() {
  const { t } = useTranslation('ponto');
  const s = useSessao();
  const [estado, setEstado] = useState<EstadoAjuste | ''>('');
  const [aberto, setAberto] = useState<Ajuste | 'novo' | null>(null);
  const colunas: Coluna<Ajuste>[] = [
    { chave: 'funcionario_nome', rotulo: t('campos.funcionario'), principal: true, render: (a) => <span className="font-medium">{nomePessoa(a.funcionario_nome) || '-'}</span> },
    { chave: 'tipo', rotulo: t('campos.tipo'), render: (a) => t(`tipoAjuste.${a.tipo}`) },
    { chave: 'data_referencia', rotulo: t('campos.dia'), render: (a) => data(a.data_referencia) },
    { chave: 'hora', rotulo: t('campos.hora'), mono: true, render: (a) => a.hora ?? '-' },
    { chave: 'estado', rotulo: t('campos.estado'), render: (a) => <Selo cor={COR_AJUSTE[a.estado]}>{t(`estado.${a.estado}`)}</Selo> },
    { chave: 'justificativa', rotulo: t('campos.justificativa'), oculta: true, render: (a) => textoLivre(a.justificativa, a.funcionario_nome) },
  ];
  const params = useMemo(() => (estado ? { estado } : {}), [estado]);
  return (
    <>
      {s.pode('ponto.criar') && (
        <AcaoPrimaria>
          <Botao variante="primario" icone={<Plus className="size-4" />} onClick={() => setAberto('novo')}>
            {t('acoes.novoAjuste')}
          </Botao>
        </AcaoPrimaria>
      )}
      <Tabela<Ajuste>
        tela="ponto.ajustes"
        caminho="/ponto/ajustes"
        params={params}
        chaveConsulta={['ponto', 'ajustes', estado]}
        colunas={colunas}
        aoClicar={(a) => setAberto(a)}
        buscaNaUrl
        filtros={{ estado }}
        aoAplicarFiltros={(f) => setEstado(ESTADOS_AJUSTE.includes(f.estado as EstadoAjuste) ? (f.estado as EstadoAjuste) : '')}
        filtrosExtra={
          <Selecao
            aria-label={t('campos.estado')}
            value={estado}
            onChange={(e) => setEstado(e.target.value as EstadoAjuste | '')}
            opcoes={[{ valor: '', rotulo: t('estado.todos') }, ...ESTADOS_AJUSTE.map((x) => ({ valor: x, rotulo: t(`estado.${x}`) }))]}
          />
        }
        vazio={<Vazio texto={t('vazio.ajustes')} />}
      />
      {aberto === 'novo' && <NovoAjuste aoFechar={() => setAberto(null)} />}
      {aberto && aberto !== 'novo' && <FichaAjuste ajuste={aberto} aoFechar={() => setAberto(null)} />}
    </>
  );
}

function NovoAjuste({ aoFechar }: { aoFechar: () => void }) {
  const { t } = useTranslation('ponto');
  const { t: tn } = useTranslation();
  const s = useSessao();
  const qc = useQueryClient();
  const toast = useToast();
  const outro = s.pode('ponto.editar');
  const campos: DefCampo[] = [
    ...(outro
      ? [{ nome: 'funcionario_id', rotulo: t('campos.funcionario'), tipo: 'busca', ajuda: t('campos.funcionarioAjuda'), busca: { caminho: '/pessoas/funcionarios', rotulo: (i: Record<string, unknown>) => nomePessoa(String(i.nome ?? '')), pessoa: true } } as DefCampo]
      : []),
    { nome: 'momento', rotulo: t('campos.momento'), tipo: 'datahora', obrigatorio: true },
    { nome: 'justificativa', rotulo: t('campos.justificativa'), tipo: 'area', obrigatorio: true, ajuda: t('campos.justificativaAjuda') },
  ];
  return (
    <Drawer
      aberto
      aoFechar={aoFechar}
      titulo={t('tipoAjuste.incluir')}
      rodape={
        <>
          <Botao variante="fantasma" onClick={aoFechar}>
            {tn('comum.cancelar')}
          </Botao>
          <Botao variante="primario" type="submit" form="form-ajuste-ponto">
            {tn('comum.salvar')}
          </Botao>
        </>
      }
    >
      <Formulario
        id="form-ajuste-ponto"
        campos={campos}
        aoEnviar={async (v) => {
          // O campo de data e hora vem sem fuso: vale o do navegador (o mesmo da tela).
          const momento = new Date(String(v.momento)).toISOString();
          await post('/ponto/ajustes', { id: crypto.randomUUID(), funcionario_id: v.funcionario_id || null, tipo: 'incluir', momento, justificativa: v.justificativa });
          await invalidarPonto(qc);
          toast.sucesso(tn('comum.salvo'));
          aoFechar();
        }}
      />
    </Drawer>
  );
}

function FichaAjuste({ ajuste: a, aoFechar }: { ajuste: Ajuste; aoFechar: () => void }) {
  const { t } = useTranslation('ponto');
  const { t: tn } = useTranslation();
  const qc = useQueryClient();
  const toast = useToast();
  const [recusando, setRecusando] = useState(false);
  const [motivo, setMotivo] = useState('');
  const [cancelando, setCancelando] = useState(false);
  const acao = useMutation({
    mutationFn: ({ qual, corpo }: { qual: 'aprovar' | 'recusar' | 'cancelar'; corpo?: unknown }) => post(`/ponto/ajustes/${a.id}/${qual}`, corpo),
    onSuccess: async () => {
      await invalidarPonto(qc);
      toast.sucesso(tn('comum.salvo'));
      aoFechar();
    },
    onError: (e) => {
      setCancelando(false);
      toast.erro(mensagemDeErro(e));
    },
  });
  const linha = (rotulo: string, valor: React.ReactNode) => (
    <div className="flex flex-wrap justify-between gap-2 border-b border-borda py-2 text-sm last:border-0">
      <dt className="text-tinta-3">{rotulo}</dt>
      <dd className="min-w-0 text-right text-tinta-1">{valor}</dd>
    </div>
  );
  return (
    <>
      <Drawer
        aberto={!recusando}
        aoFechar={aoFechar}
        titulo={`${nomePessoa(a.funcionario_nome)} · ${t(`tipoAjuste.${a.tipo}`)}`}
        rodape={
          <>
            {a.pode_cancelar && (
              <Botao variante="perigo" onClick={() => setCancelando(true)}>
                {t('acoes.cancelarAjuste')}
              </Botao>
            )}
            {a.pode_decidir && (
              <>
                <Botao icone={<X className="size-4" />} onClick={() => setRecusando(true)}>
                  {t('acoes.recusar')}
                </Botao>
                <Botao variante="primario" icone={<Check className="size-4" />} carregando={acao.isPending} onClick={() => acao.mutate({ qual: 'aprovar' })}>
                  {t('acoes.aprovar')}
                </Botao>
              </>
            )}
          </>
        }
      >
        <dl>
          {linha(t('campos.estado'), <Selo cor={COR_AJUSTE[a.estado]}>{t(`estado.${a.estado}`)}</Selo>)}
          {linha(t('campos.dia'), `${data(a.data_referencia)}${a.hora ? ` ${a.hora}` : ''}`)}
          {linha(t('campos.justificativa'), textoLivre(a.justificativa, a.funcionario_nome))}
          {a.pedido_por_nome && linha(t('campos.pedidoPor'), nomePessoa(a.pedido_por_nome))}
          {a.decidido_em && linha(t('campos.decididoPor'), `${nomePessoa(a.decidido_por_nome) || '-'} · ${dataHora(a.decidido_em)}`)}
          {a.motivo_decisao && linha(t('campos.motivo'), textoLivre(a.motivo_decisao, a.funcionario_nome))}
        </dl>
      </Drawer>
      <Modal
        aberto={recusando}
        aoFechar={() => setRecusando(false)}
        titulo={t('recusar.titulo')}
        rodape={
          <>
            <Botao variante="fantasma" onClick={() => setRecusando(false)}>
              {tn('comum.cancelar')}
            </Botao>
            <Botao variante="primario" carregando={acao.isPending} aria-disabled={!motivo.trim()} onClick={() => motivo.trim() && acao.mutate({ qual: 'recusar', corpo: { motivo } })}>
              {t('acoes.recusar')}
            </Botao>
          </>
        }
      >
        <Rotulo id="ponto-motivo" rotulo={t('campos.motivo')} obrigatorio ajuda={t('recusar.ajuda')}>
          <AreaSensivel
            id="ponto-motivo"
            rows={3}
            maxLength={500}
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
            className="w-full min-w-0 rounded-lg border border-borda-campo bg-campo px-3 py-2 text-sm text-tinta-1 focus:border-foco focus:outline-2 focus:outline-offset-0 focus:outline-foco"
          />
        </Rotulo>
      </Modal>
      <Confirmar aberto={cancelando} aoFechar={() => setCancelando(false)} aoConfirmar={() => acao.mutate({ qual: 'cancelar' })} titulo={t('confirmar.cancelar')} texto={t('confirmar.cancelarTexto')} perigoso carregando={acao.isPending} />
    </>
  );
}
